import { useContext, useEffect, useRef, useState } from 'react';
import TabButton from '../../../../Components/TabButton/TabButton';
import FilterIcon from "../../../../Assets/Filter.svg";
import SortIcon from "../../../../Assets/Switch vertical.svg";
import { useDispatch, useSelector } from 'react-redux';
import FilterKebab from './FilterKebab';
import { getCategoriesForStore, getCollectionForCategory, getTags } from '../../../../lib/utils/apiCalls';
import SortKebab from './SortKebab';
import TickMark from '../../../../Assets/Modals/TickMark.svg';
import ProductBar from './ProductBar';
import { setModal } from '../../../../Features/inventoryManagementSlice';
import Minus from "../../../../Assets/MinusCheckbox.svg";
import { useOutsideClickForTwoRef } from '../../../../Hooks/useOutsideClickForTwoRef';
import { InventoryManagementContext } from '../../../../Context/InventoryManagementContext';

const tabs = [
    {
        id: 'all',
        name: 'All Products',
    },
    {
        id: 'active',
        name: 'Active',
    },
    {
        id: 'draft',
        name: 'Draft',
    },
    {
        id: 'out-of-stock',
        name: 'Out of Stock',
    },
    {
        id: 'inactive',
        name: 'Inactive',
    }
];

const getStatus = (status) => {
    switch (status) {
        case 'Active':
            return 'Active';
        case 'Draft':
            return 'Draft';
        case 'Out Of Stock':
            return 'Out of Stock';
        case 'InActive':
            return 'Inactive';
        case 'Inactive':
            return 'Inactive';
        default:
            return '';
    }
}

function sortProducts(products, filter) {
    const sortedProducts = [...products];

    if (filter === 'high-to-low') {
        sortedProducts.sort((a, b) => b.product.productMRP - a.product.productMRP);
    }
    else if (filter === 'low-to-high') {
        sortedProducts.sort((a, b) => a.product.productMRP - b.product.productMRP);
    }
    else if (filter === 'newly-added') {
        sortedProducts.sort((a, b) => new Date(b.product.createdAt) - new Date(a.product.createdAt));
    }

    return sortedProducts;
}

function Products() {
    const dispatch = useDispatch();

    const filterButtonRef = useRef(null);
    const filterListRef = useRef(null);

    useOutsideClickForTwoRef(filterButtonRef, filterListRef, () => {
        setKebabOpen(false);
        setKebabType('');
    });

    const sortButtonRef = useRef(null);
    const sortListRef = useRef(null);

    useOutsideClickForTwoRef(sortButtonRef, sortListRef, () => {
        setKebabOpen(false);
        setKebabType('');
    });

    const [activeTab, setActiveTab] = useState('all');

    const [categories, setCategories] = useState([]);
    const [collections, setCollections] = useState([]);
    const [tags, setTags] = useState([]);

    const [productsList, setProductsList] = useState([]);

    const [kebabOpen, setKebabOpen] = useState(false);
    const [kebabType, setKebabType] = useState('');
    const [checkboxType, setCheckboxType] = useState('');

    const { products } = useSelector((state) => state.inventoryManagement.products);

    const { selectedProducts, setSelectedProducts } = useContext(InventoryManagementContext);

    const filters = useSelector((state) => state.inventoryManagement.filters);

    const handleTabSelect = (selection) => {
        setActiveTab(selection);
    }

    const handleFilterClick = () => {
        if(kebabType === 'filter') {
            setKebabOpen(false);
            setKebabType('');
        }
        else {
            setKebabOpen(true);
            setKebabType('filter');
        }
    }

    const handleSortClick = () => {
        if(kebabType === 'sort') {
            setKebabOpen(false);
            setKebabType('');
        }
        else {
            setKebabOpen(true);
            setKebabType('sort');
        }
    }

    const handleCheckboxClick = () => {
        if(selectedProducts.length < productsList.length) {
            if(selectedProducts.length === 0) {
                let listId = productsList.map(item => item.product.id);
                setSelectedProducts(listId);
            }
            else {
                setSelectedProducts([]);
            }
        }
        else {
            setSelectedProducts([]);
        }
    }

    useEffect(() => {
        if(selectedProducts.length < productsList.length) {
            if(selectedProducts.length === 0) {
                setCheckboxType('');
            }
            else {
                setCheckboxType('half')
            }
        }
        else if(selectedProducts.length === 0) {
            setCheckboxType('');
        }
        else {
            setCheckboxType('full');
        }
    }, [selectedProducts, productsList]);

    useEffect(() => {
        const fetchStoreCategories = async () => {
            try {
                const response = await getCategoriesForStore();
                setCategories(response.category);
            }
            catch(err) {
                console.log(err);
            }
        }

        const fetchStoreTags = async () => {
            try {
                const response = await getTags();
                setTags(response.tags);
            }
            catch(err) {
                console.log(err);
            }
        }

        fetchStoreTags();
        fetchStoreCategories();
    }, []);

    useEffect(() => {
        const fetchCollections = async () => {
            if (categories?.length > 0) {
                const categoryIds = categories.map(category => category.id);

                let list = [];

                for (const id of categoryIds) {
                    const response = await getCollectionForCategory(id);
                    
                    list = [...list, ...response.collection];
                }

                setCollections(list);
            }
        };

        fetchCollections();
    }, [categories]);

    let filtersLength = filters.selectedCategories?.length + filters.selectedCollections?.length + filters.selectedTags?.length;

    const handleDeleteList = () => {
        dispatch(setModal('delete-confirm'));
    }

    const handleHideList = () => {
        dispatch(setModal('hide-confirm'));
    }

    const handleAddProductOrg = () => {
        dispatch(setModal('add-product-org'));
    }

    const handleAddTags = () => {
        dispatch(setModal('add-tags'));
    }

    useEffect(() => {
        let tabProducts;

        switch (activeTab) {
            case 'active':
                tabProducts = products.filter(item => item.product.status === 'Active');
                break;
            case 'draft':
                tabProducts = products.filter(item => item.product.status === 'Draft');
                break;
            case 'out-of-stock':
                tabProducts = products.filter(item => item.product.status === 'Out Of Stock');
                break;
            case 'inactive':
                tabProducts = products.filter(item => item.product.status === 'Inactive');
                break;
            case 'all':
            default:
                tabProducts = products;
                break;
        }

        let filteredProducts;

        if(filtersLength) {
            let locallyFilteredProducts = [];
            let filteredProductsByCategories = [];
            let filteredProductsByCollections = [];
            let filteredProductsByTags = [];

            if(filters.selectedCategories?.length > 0) {
                const selectedCategoryNames = filters.selectedCategories.map(category => category.categoryName);

                filteredProductsByCategories = tabProducts.filter(product => 
                    product.categoryName.some(category => selectedCategoryNames.includes(category))
                );

                locallyFilteredProducts = [ ...locallyFilteredProducts, ...filteredProductsByCategories ];
            }

            if(filters.selectedCollections?.length > 0) {
                const selectedCollectionNames = filters.selectedCollections.map(collection => collection.CollectionName);

                filteredProductsByCollections = tabProducts.filter(product => 
                    product.collectionName.some(collection => selectedCollectionNames.includes(collection))
                );

                locallyFilteredProducts = [ ...locallyFilteredProducts, ...filteredProductsByCollections ];
            }

            if(filters.selectedTags?.length > 0) {
                filteredProductsByTags = products.filter(product => 
                    product.product.Tag.some(tag => filters.selectedTags.includes(tag))
                );

                locallyFilteredProducts = [ ...locallyFilteredProducts, ...filteredProductsByTags ];
            }

            filteredProducts = [ ...new Set(locallyFilteredProducts)];
        }
        else {
            filteredProducts = tabProducts;
        }

        if(filters.sortType.length > 0) {
            const sortedProducts = sortProducts(filteredProducts, filters.sortType);
            setProductsList(sortedProducts);
        }
        else {
            setProductsList(filteredProducts);
        }
    }, [activeTab, products, filtersLength, filters]);

    return (
        <div className="flex flex-col select-none">
            <div className='flex items-center justify-between border-b-2 border-[#F5F5F5] mt-6 mx-5'>
                <div className="flex">
                    {tabs.map(tab => {
                        return (
                            <TabButton 
                                key={tab.id} 
                                id={tab.id} 
                                optionName={tab.name} 
                                selected={activeTab} 
                                handleSelect={handleTabSelect} 
                            />
                        )
                    })}
                </div>

                <div className="flex items-center gap-2">
                    <div ref={filterButtonRef} onClick={handleFilterClick} className='relative flex items-center gap-[2px] p-[4px_8px_4px_3px] outline-none font-medium text-[14px] leading-6 text-[#666666] cursor-pointer'>
                        <div className="size-6 flex items-center justify-center">
                            <img src={FilterIcon} alt="" />
                        </div>

                        Filter

                        {
                            kebabType === 'filter' && 
                            <FilterKebab 
                                categories={categories} 
                                collections={collections} 
                                tags={tags} 
                                kebabOpen={kebabOpen} 
                                boxRef={filterListRef} 
                            />
                        }

                        {
                            filtersLength > 0 &&
                            <div className="ml-[2px] size-4 flex items-center justify-center bg-primaryColor text-[12px] leading-3 font-semibold text-[#FFFFFF] rounded-full">
                                {filtersLength}
                            </div>
                        }
                    </div>

                    <div ref={sortButtonRef} onClick={handleSortClick} className='relative flex items-center gap-[2px] p-[4px_8px_4px_3px] outline-none font-medium text-[14px] leading-6 text-[#666666] cursor-pointer'>
                        <div className="size-6 flex items-center justify-center">
                            <img src={SortIcon} alt="" />
                        </div>

                        Sort by

                        {
                            kebabType === 'sort' && 
                            <SortKebab 
                                kebabOpen={kebabOpen} 
                                boxRef={sortListRef} 
                            />
                        }
                    </div>
                </div>
            </div>

            <div className="flex flex-col mt-6 mx-5">
                <div className="flex bg-[#F5F5F5] font-semibold text-[#666666]">
                    <div className="flex items-center gap-2 w-[calc((35/100)*(100%-44px))] px-[10px] py-[8px] text-[14px] leading-6 uppercase">
                        {
                            productsList.length > 0 && 
                            <div onClick={handleCheckboxClick} className={`cursor-pointer size-6 bg-[#FFFFFF] flex items-center justify-center rounded-md border ${checkboxType !== '' ? 'border-none bg-primaryColor' : 'border-[#D1D5DB]'} `} >
                                <img className='select-none' src={checkboxType === 'full' ? TickMark : checkboxType === '' ? undefined : Minus} alt="" />
                            </div>
                        }

                        <h2 className="">product</h2>
                    </div>

                    <h2 className="w-[calc((14/100)*(100%-44px))] px-[10px] py-[8px] text-[14px] leading-6 uppercase">sku code</h2>
                    <h2 className="w-[calc((10/100)*(100%-44px))] px-[10px] py-[8px] text-[14px] leading-6 uppercase">price</h2>
                    <h2 className="w-[calc((13/100)*(100%-44px))] px-[10px] py-[8px] text-[14px] leading-6 uppercase">status</h2>
                    <h2 className="w-[calc((15/100)*(100%-44px))] px-[10px] py-[8px] text-[14px] leading-6 uppercase">size chart</h2>
                    <h2 className="w-[calc((13/100)*(100%-44px))] px-[10px] py-[8px] text-[14px] leading-6 uppercase">mark active</h2>
                    <div className="w-[44px]"></div>
                </div>

                {productsList?.sort((a, b) =>
                        a.product.productName.localeCompare(b.product.productName)
                    ).map(product => {
                    const isSelected = selectedProducts.includes(product.product.id);
                    const status = getStatus(product.product.status);

                    return (
                        <ProductBar 
                            key={product.product.id} 
                            product={product} 
                            status={status} 
                            isSelected={isSelected} 
                        />
                    )
                })}
            </div>

            {
                selectedProducts.length > 0 && 
                <div className="fixed left-1/2 -translate-x-1/2 bottom-12 border border-[#EAEAEA] bg-[#F5F5F5] rounded-xl p-4 flex items-center gap-3">
                    <p className='font-medium text-[14px] leading-5 text-[#121212]'>{selectedProducts.length}/{productsList.length}</p>

                    <button onClick={handleDeleteList} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080] outline-none select-none'>Delete</button>

                    <button onClick={handleHideList} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080] outline-none select-none'>Hide</button>

                    <button onClick={handleAddProductOrg} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080] outline-none select-none'>Add to product org</button>

                    <button onClick={handleAddTags} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080] outline-none select-none'>Add tags</button>
                </div>
            }
        </div>
    );
}

export default Products;
